import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import {
  Calendar,
  Clock,
  User,
  ArrowLeft,
  ArrowRight,
  Phone,
  Share2,
  CheckCircle2
} from 'lucide-react';
import { SEO, BUSINESS_INFO } from '../SEO';
import { getCategoryName, fetchBlogPost, fetchBlogPosts, getBlogPostPath } from '../blog/blogData';

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

const isSafeLinkHref = (href) => {
  const trimmed = href.trim();
  return /^(https?:\/\/|\/|#|mailto:|tel:)/i.test(trimmed) && !/^javascript:/i.test(trimmed);
};

const renderLinkedText = (text) => {
  if (!text) return null;

  const nodes = [];
  const linkPattern = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match;

  while ((match = linkPattern.exec(text)) !== null) {
    const [raw, label, href] = match;
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    const safeHref = href.trim();
    if (isSafeLinkHref(safeHref)) {
      const isExternal = /^https?:\/\//i.test(safeHref);
      nodes.push(
        <a
          key={`${safeHref}-${match.index}`}
          href={safeHref}
          className="font-semibold text-blue-700 underline decoration-blue-300 underline-offset-4 hover:text-blue-900"
          {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        >
          {label}
        </a>
      );
    } else {
      nodes.push(raw);
    }

    lastIndex = match.index + raw.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
};

const getPostSchema = (post) => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: post.title,
  description: post.excerpt,
  image: post.cover,
  datePublished: post.date,
  dateModified: post.date,
  url: `${BUSINESS_INFO.url}${getBlogPostPath(post.slug)}`,
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': `${BUSINESS_INFO.url}${getBlogPostPath(post.slug)}`
  },
  author: {
    '@type': 'Organization',
    name: post.author,
    url: BUSINESS_INFO.url
  },
  publisher: {
    '@type': 'Organization',
    name: BUSINESS_INFO.name,
    logo: {
      '@type': 'ImageObject',
      url: BUSINESS_INFO.logo
    }
  },
  keywords: (post.tags || []).join(', ')
});

export const BlogPostPage = () => {
  const { slug, blogSlug } = useParams();
  const postSlug = slug || blogSlug;
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const [single, all] = await Promise.all([
          fetchBlogPost(postSlug),
          fetchBlogPosts()
        ]);
        if (!cancelled) {
          setPost(single);
          setAllPosts(all);
        }
      } catch (e) {
        if (!cancelled) {
          if (e?.response?.status === 404) setNotFound(true);
          else setNotFound(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [postSlug]);

  const related = useMemo(() => {
    if (!post) return [];
    const sameCat = allPosts.filter(
      (p) => p.slug !== post.slug && p.category === post.category
    );
    const otherCat = allPosts.filter(
      (p) => p.slug !== post.slug && p.category !== post.category
    );
    return [...sameCat, ...otherCat].slice(0, 3);
  }, [post, allPosts]);

  if (loading) {
    return (
      <div
        className="min-h-screen bg-white pt-32 text-center text-gray-500"
        data-testid="blog-post-loading"
      >
        Loading article...
      </div>
    );
  }

  if (notFound || !post) {
    return <Navigate to="/blog" replace />;
  }

  const handleShare = async () => {
    const shareData = {
      title: post.title,
      text: post.excerpt,
      url: window.location.href
    };
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (e) {
        // user cancelled
      }
    } else {
      navigator.clipboard?.writeText(window.location.href);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20" data-testid={`blog-post-${post.slug}`}>
      <SEO
        title={post.title}
        description={post.excerpt}
        keywords={post.keywords}
        canonical={getBlogPostPath(post.slug)}
        ogType="article"
        ogImage={post.cover}
        schema={[getPostSchema(post)]}
      />

      {/* Hero */}
      <section className="relative">
        <div
          className="h-[380px] sm:h-[460px] bg-cover bg-center"
          style={{ backgroundImage: `url(${post.cover})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        </div>

        <div className="container mx-auto px-4">
          <div className="-mt-40 sm:-mt-52 relative z-10 max-w-4xl">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-4 text-sm"
              data-testid="blog-back-link"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Blog
            </Link>

            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Badge className="bg-bright-orange hover:bg-bright-orange text-white">
                {getCategoryName(post.category)}
              </Badge>
              {(post.tags || []).slice(0, 2).map((tag) => (
                <Badge
                  key={tag}
                  variant="secondary"
                  className="bg-white/20 backdrop-blur-sm text-white border-white/30"
                >
                  {tag}
                </Badge>
              ))}
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
              {post.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-white/90 text-sm">
              <span className="flex items-center gap-1">
                <User className="h-4 w-4" />
                {post.author}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                {formatDate(post.date)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {post.readTime}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="container mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          <article className="lg:col-span-2 space-y-6" data-testid="blog-post-content">
            <p className="text-xl text-gray-700 leading-relaxed font-medium border-l-4 border-sky-blue pl-4">
              {renderLinkedText(post.excerpt)}
            </p>

            {(post.content || []).map((block, idx) => {
              if (block.type === 'heading') {
                return (
                  <h2
                    key={idx}
                    className="text-2xl sm:text-3xl font-bold text-gray-900 mt-10 mb-4 leading-tight"
                  >
                    {renderLinkedText(block.text)}
                  </h2>
                );
              }
              if (block.type === 'paragraph') {
                return (
                  <p
                    key={idx}
                    className="text-gray-700 leading-relaxed text-base sm:text-lg"
                  >
                    {renderLinkedText(block.text)}
                  </p>
                );
              }
              if (block.type === 'list' && Array.isArray(block.items)) {
                return (
                  <ul key={idx} className="space-y-3 my-4">
                    {block.items.map((item, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-3 text-gray-700 text-base sm:text-lg leading-relaxed"
                      >
                        <CheckCircle2 className="h-5 w-5 text-sky-blue mt-1 flex-shrink-0" />
                        <span>{renderLinkedText(item)}</span>
                      </li>
                    ))}
                  </ul>
                );
              }
              if (block.type === 'image' && block.text) {
                return (
                  <figure key={idx} className="my-8" data-testid={`blog-image-block-${idx}`}>
                    <img
                      src={block.text}
                      alt={block.caption || 'Blog illustration'}
                      className="w-full rounded-xl shadow-md"
                      loading="lazy"
                    />
                    {block.caption && (
                      <figcaption className="text-sm text-gray-500 text-center mt-3 italic">
                        {block.caption}
                      </figcaption>
                    )}
                  </figure>
                );
              }
              return null;
            })}

            <div className="pt-8 border-t mt-12">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex flex-wrap gap-2">
                  {(post.tags || []).map((tag) => (
                    <Badge key={tag} variant="outline" className="text-gray-600">
                      #{tag}
                    </Badge>
                  ))}
                </div>
                <Button
                  variant="outline"
                  onClick={handleShare}
                  data-testid="blog-share-button"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Share Article
                </Button>
              </div>
            </div>
          </article>

          <aside className="space-y-6">
            <Card
              className="border-0 shadow-lg text-white sticky top-28"
              style={{
                background:
                  'linear-gradient(135deg, #1e3a8a 0%, #2563eb 60%, #0891b2 100%)'
              }}
            >
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-3">Get a Free Quote</h3>
                <p className="text-white/90 text-sm mb-5 leading-relaxed">
                  Planning a move or cleaning in Indiana? Tell us about your project and we'll send
                  over a detailed quote within 2 hours.
                </p>
                <Button
                  className="bg-bright-orange hover:bg-orange-600 text-white w-full mb-3"
                  onClick={() => (window.location.href = '/#booking')}
                  data-testid="blog-sidebar-quote-button"
                >
                  Request Quote
                </Button>
                <Button
                  variant="outline"
                  className="border-white text-white hover:bg-white hover:text-navy-blue w-full"
                  onClick={() => (window.location.href = 'tel:8126694165')}
                  data-testid="blog-sidebar-call-button"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  (812) 669-4165
                </Button>
              </CardContent>
            </Card>
          </aside>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-light-gray py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Related Articles</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {related.map((rp) => (
                <Link
                  key={rp.slug}
                  to={getBlogPostPath(rp.slug)}
                  className="group"
                  data-testid={`blog-related-${rp.slug}`}
                >
                  <Card className="overflow-hidden h-full border-0 shadow-md hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1">
                    <div
                      className="h-40 bg-cover bg-center"
                      style={{ backgroundImage: `url(${rp.cover})` }}
                    />
                    <CardContent className="p-5">
                      <Badge
                        variant="outline"
                        className="text-sky-blue border-sky-blue mb-2"
                      >
                        {getCategoryName(rp.category)}
                      </Badge>
                      <h3 className="text-base font-bold text-gray-900 group-hover:text-sky-blue transition-colors leading-snug line-clamp-2">
                        {rp.title}
                      </h3>
                      <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {rp.readTime}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>

            <Separator className="my-12" />

            <div className="text-center">
              <Link to="/blog">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-navy-blue text-navy-blue hover:bg-navy-blue hover:text-white"
                  data-testid="blog-view-all-button"
                >
                  View All Articles
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPostPage;
